import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import type { MapSettingsDataLayersAction } from './mapSettingsDataLayers.action';
import {
  mapSettingsDataLayersAddDataLayer,
  mapSettingsDataLayersRemoveDataLayer,
  mapSettingsDataLayersUpdateBoundaryGroup,
  mapSettingsDataLayersUpdateDataLayer,
  mapSettingsDataLayersUpdateIndividualLayerSettings,
} from './mapSettingsDataLayers.actionCreators';
import type { MapSettingsDataLayersState } from './mapSettingsDataLayers.state';

export const mapSettingsDataLayersInitialState: MapSettingsDataLayersState = {
  instances: [],
  boundaryGroup: {},
  individualLayerSettings: {},
};

export const mapSettingsDataLayersReducer = (
  state = mapSettingsDataLayersInitialState,
  action: MapSettingsDataLayersAction
): MapSettingsDataLayersState => {
  if (mapSettingsDataLayersAddDataLayer.match(action)) {
    return {
      ...state,
      instances: [action.payload.dataLayer], //for now we allow only one instance at a time
    };
  }

  if (mapSettingsDataLayersRemoveDataLayer.match(action)) {
    return {
      ...state,
      instances: state.instances.filter((instance) => action.payload.dataLayer.id !== instance.id),
      boundaryGroup: Object.fromEntries(
        stringObjectEntries(state.boundaryGroup)
          .filter(([dataLayerId]) => dataLayerId !== action.payload.dataLayer.id)
      ),
    };
  }

  if (mapSettingsDataLayersUpdateDataLayer.match(action)) {
    return {
      ...state,
      instances: state.instances.map((instance) =>
        instance.id === action.payload.id
          ? {
            ...instance,
            ...(action.payload.data && { data: action.payload.data }),
            ...(action.payload.settings && { settings: action.payload.settings }),
            ...(action.payload.fromTemplate && { fromTemplate: action.payload.fromTemplate }),
          }
          : instance
      ),
    };
  }

  if (mapSettingsDataLayersUpdateBoundaryGroup.match(action)) {
    return {
      ...state,
      boundaryGroup: {
        ...state.boundaryGroup,
        [action.payload.dataLayerId]: action.payload.boundaryGroupId,
      },
    };
  }

  if (mapSettingsDataLayersUpdateIndividualLayerSettings.match(action)) {
    const { boundaryGroupId, demographicId, settings } = action.payload;

    const currentSettings = state.individualLayerSettings[boundaryGroupId]?.[demographicId];

    return {
      ...state,
      individualLayerSettings: {
        ...state.individualLayerSettings,
        [boundaryGroupId]: {
          ...state.individualLayerSettings[boundaryGroupId],
          [demographicId]: {
            ...currentSettings,
            individualSettingsActive: settings.individualSettingsActive,
            boundarySettings: {
              ...currentSettings?.boundarySettings,
              ...settings.boundarySettings,
            },
          },
        },
      },
    };
  }

  return state;
};
